import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportingLeLensCompanySharingPath, reportingLeLensSharingPath } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { Lens, LensShareRecipient } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';
import { lensSpaceQueryKeys } from './useLensSpaces';

export type UpdateLensSharingInput = {
  lensId: string;
  /** The whole recipient list. Anyone left out loses their direct share. */
  /** Add or upgrade access without removing existing recipients. */
  grants?: Array<{ email: string; role: LensShareRecipient['role'] }>;
  recipients?: Array<{ email: string; role: LensShareRecipient['role'] }>;
  /** The new home. Null returns the Lens to Personal; omitted leaves it where it is. */
  spaceId?: string | null;
  /** The new personal manager, required when moving a Lens out of a team space. */
  ownerEmail?: string;
};

/**
 * Writes a Lens's audience and home together, because the dialog presents them as
 * one decision. Publishing to the company is a separate promotion with its own
 * confirmation and uses its own company-sharing endpoint.
 */
export function useUpdateLensSharing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ lensId, ...body }: UpdateLensSharingInput) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensSharingPath(lensId)), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })) as { lens: Lens; recipients: Array<LensShareRecipient> },
    onSuccess: async (_result, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(variables.lensId) }),
        queryClient.invalidateQueries({ queryKey: lensSpaceQueryKeys.all }),
      ]);
    },
  });
}

/** A reporting-admin-only company promotion, enforced again by the service. */
export function useShareLensWithCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (lensId: string) =>
      (await request(resolveReportingLeLensUrl(reportingLeLensCompanySharingPath(lensId)), {
        method: 'POST',
      })) as { lens: Lens },
    onSuccess: async (_result, lensId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId) }),
      ]);
    },
    retry: false,
  });
}
